import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError } from '@/api/errors';
import { SESSION_EXPIRED_EVENT } from '@/api/http';
import { queryKeys } from '@/api/query-keys';
import { tokenStore } from '@/api/token-store';

import { authApi, currentDeviceName } from './api';
import type { Permission, RoleName } from './permissions';
import { userHasAnyPermission, userHasPermission, userHasRole, type AuthUser } from './types';

interface AuthContextValue {
  user: AuthUser | null;
  /** True until the initial `me` call settles, so guards can wait instead of bouncing to login. */
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (input: { email: string; password: string; remember: boolean }) => Promise<AuthUser>;
  logout: () => Promise<void>;
  isLoggingIn: boolean;
  can: (permission: Permission) => boolean;
  canAny: (permissions: readonly Permission[]) => boolean;
  hasRole: (role: RoleName) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [hasToken, setHasToken] = useState(() => tokenStore.get() !== null);

  useEffect(
    () =>
      tokenStore.subscribe(() => {
        setHasToken(tokenStore.get() !== null);
      }),
    [],
  );

  // A 401 on any request clears the token in the interceptor. Drop the cached
  // user in the same beat so the UI does not keep rendering a logged-in shell
  // whose every request now fails.
  useEffect(() => {
    const onExpired = (): void => {
      queryClient.setQueryData(queryKeys.session.current(), null);
      void queryClient.invalidateQueries();
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
    };
  }, [queryClient]);

  const sessionQuery = useQuery({
    queryKey: queryKeys.session.current(),
    queryFn: ({ signal }) => authApi.me(signal),
    enabled: hasToken,
    staleTime: 5 * 60_000,
    retry: (failureCount, error) =>
      error instanceof ApiError && error.isRetryable && failureCount < 1,
  });

  const loginMutation = useMutation({
    mutationFn: (input: { email: string; password: string; remember: boolean }) =>
      authApi.login({ ...input, device_name: currentDeviceName() }),
    onSuccess: (response, variables) => {
      tokenStore.set(response.token, { remember: variables.remember });
      queryClient.setQueryData(queryKeys.session.current(), response.user);
    },
    // Login failures are rendered inline on the form, not as a toast.
    onError: () => undefined,
  });

  const login = useCallback(
    async (input: { email: string; password: string; remember: boolean }) => {
      const response = await loginMutation.mutateAsync(input);
      return response.user;
    },
    [loginMutation],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // A failed logout must still log the editor out locally, otherwise a
      // network blip leaves them stuck in a session they asked to end. The token
      // expires server-side regardless.
    } finally {
      tokenStore.clear();
      queryClient.setQueryData(queryKeys.session.current(), null);
      queryClient.clear();
    }
  }, [queryClient]);

  const user = sessionQuery.data ?? null;

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading: hasToken && sessionQuery.isPending,
      isAuthenticated: user !== null,
      login,
      logout,
      isLoggingIn: loginMutation.isPending,
      can: (permission) => userHasPermission(user, permission),
      canAny: (permissions) => userHasAnyPermission(user, permissions),
      hasRole: (role) => userHasRole(user, role),
    }),
    [user, hasToken, sessionQuery.isPending, login, logout, loginMutation.isPending],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const context = use(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside <AuthProvider>.');
  }

  return context;
}
